import { useState, useEffect } from 'react';
import { categoriesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  Loader2,
  Tag
} from 'lucide-react';

const EMOJI_OPTIONS = [
  '🏪', '🛒', '👗', '👔', '👶', '👟', '💍', '📱', '🏠', '🍕', '🍔', '🥩', '🍽️',
  '🚗', '🔑', '🧱', '💊', '📲', '🥖', '☕', '🍰', '🎮', '💻', '📺', '🛋️', '🌸',
  '💄', '🧴', '🏋️', '⚽', '🎸', '📚', '🎨', '🐕', '🐈', '🌿', '💐', '🎁', '✨'
];

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#d946ef', '#ec4899', '#f43f5e', '#64748b', '#1e40af', '#0f172a'
];

const INITIAL_FORM = {
  name: '',
  icon: '🏪',
  color: '#3b82f6',
  order: 0
};

export const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const { data } = await categoriesAPI.list();
      setCategories(data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, formData);
        toast.success('Categoria atualizada!');
      } else {
        await categoriesAPI.create({ ...formData, order: categories.length + 1 });
        toast.success('Categoria criada!');
      }
      
      setDialogOpen(false);
      setFormData(INITIAL_FORM);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      toast.error('Erro ao salvar categoria');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon,
      color: category.color,
      order: category.order
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await categoriesAPI.delete(categoryToDelete.id);
      toast.success('Categoria excluída!');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      loadCategories();
    } catch (error) {
      toast.error('Erro ao excluir categoria');
    }
  };

  return (
    <div className="space-y-6" data-testid="categories-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Categorias</h1>
          <p className="text-slate-500 mt-1">Gerencie as categorias de lojas e produtos</p>
        </div>
        <Button
          onClick={() => { setEditingCategory(null); setFormData(INITIAL_FORM); setDialogOpen(true); }}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      {/* Categories Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : categories.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
          <Tag className="w-12 h-12 mb-4" />
          <p className="text-lg font-medium">Nenhuma categoria</p>
          <p className="text-sm">Crie sua primeira categoria</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {categories.map((category) => (
            <Card
              key={category.id}
              className="overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
              data-testid={`category-card-${category.id}`}
            >
              <div
                className="aspect-square flex flex-col items-center justify-center text-white p-4"
                style={{ backgroundColor: category.color }}
              >
                <span className="text-4xl mb-2">{category.icon}</span>
                <span className="text-sm font-semibold text-center leading-tight">{category.name}</span>
              </div>
              <div className="p-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-50">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(category)} className="flex-1">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 flex-1" onClick={() => { setCategoryToDelete(category); setDeleteDialogOpen(true); }}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Categoria *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Pizzaria, Farmácia, Veículos..."
                required
                data-testid="input-name"
              />
            </div>

            {/* Icon Selector */}
            <div className="space-y-2">
              <Label>Ícone</Label>
              <div className="grid grid-cols-10 gap-1 p-2 border rounded-lg max-h-32 overflow-y-auto">
                {EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: emoji })}
                    className={`text-xl p-1 rounded hover:bg-slate-100 ${formData.icon === emoji ? 'bg-blue-100 ring-2 ring-blue-500' : ''}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selector */}
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="grid grid-cols-10 gap-1 p-2 border rounded-lg">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color })}
                    className={`w-6 h-6 rounded-full ${formData.color === color ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <Label>Prévia</Label>
              <div
                className="w-24 h-24 rounded-xl flex flex-col items-center justify-center text-white mx-auto"
                style={{ backgroundColor: formData.color }}
              >
                <span className="text-3xl">{formData.icon}</span>
                <span className="text-xs font-semibold mt-1 text-center px-1 truncate w-full">
                  {formData.name || 'Nome'}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={submitting} data-testid="submit-category-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingCategory ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Tem certeza que deseja excluir a categoria <strong>{categoryToDelete?.name}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Categories;
